import { useState } from "react";
import { Menu, X, ChevronRight, Shield, Heart, Award, Users, Phone, Mail, Clock, Check, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";

export default function Services() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedFaqs, setExpandedFaqs] = useState({});

  // Service categories
  const categories = [
    { id: "all", name: "All Services" },
    { id: "health", name: "Health Insurance" },
    { id: "life", name: "Life Insurance" },
    { id: "senior", name: "Senior Plans" },
    { id: "custom", name: "Custom Solutions" }
  ];

  // Toggle FAQ expansion
  const toggleFaq = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Services data
  const services = [
    {
      id: "health-comprehensive",
      category: "health",
      title: "Comprehensive Health Insurance",
      description: "Full-coverage health insurance plans that provide protection for a wide range of medical services and treatments.",
      features: [
        "Coverage for doctor visits and specialist consultations",
        "Hospital stays and emergency room visits",
        "Prescription medications",
        "Preventive care and wellness programs",
        "Mental health services"
      ],
      image: "health-comprehensive.png"
    },
    {
      id: "health-affordable",
      category: "health",
      title: "Affordable Health Options",
      description: "Budget-friendly health insurance plans that provide essential coverage without breaking the bank.",
      features: [
        "Low monthly premiums",
        "Essential medical services coverage",
        "Telehealth consultations",
        "Preventive care benefits",
        "Flexible deductible options"
      ],
      image: "health-affordable.png"
    },
    {
      id: "life-term",
      category: "life",
      title: "Term Life Insurance",
      description: "Temporary life insurance coverage that provides protection for a specified period of time.",
      features: [
        "Coverage periods of 10, 20, or 30 years",
        "Affordable premium rates",
        "High coverage amounts available",
        "Option to convert to permanent coverage",
        "Customizable riders for additional protection"
      ],
      image: "life-term.png"
    },
    {
      id: "life-whole",
      category: "life",
      title: "Whole Life Insurance",
      description: "Permanent life insurance coverage that provides protection for your entire lifetime with a cash value component.",
      features: [
        "Lifetime coverage guarantee",
        "Cash value accumulation",
        "Fixed premium payments",
        "Potential dividends",
        "Tax-advantaged benefits"
      ],
      image: "life-whole.png"
    },
    {
      id: "senior-medicare",
      category: "senior",
      title: "Medicare Supplement Plans",
      description: "Insurance policies that help cover the gaps in Medicare coverage, providing more comprehensive protection for seniors.",
      features: [
        "Coverage for Medicare deductibles and coinsurance",
        "Minimal out-of-pocket expenses",
        "No network restrictions with most plans",
        "Guaranteed renewable coverage",
        "Standardized benefits across plans"
      ],
      image: "senior-medicare.png"
    },
    {
      id: "senior-care",
      category: "senior",
      title: "Long-Term Care Insurance",
      description: "Insurance that helps cover the costs of long-term care services, which are typically not covered by health insurance or Medicare.",
      features: [
        "Coverage for nursing home care",
        "Home health care services",
        "Assisted living facility costs",
        "Adult day care services",
        "Respite care coverage"
      ],
      image: "senior-care.png"
    },
    {
      id: "custom-group",
      category: "custom",
      title: "Group Insurance Plans",
      description: "Customized insurance solutions for businesses, organizations, and communities to provide coverage for their members.",
      features: [
        "Tailored benefits for group needs",
        "Cost-effective coverage options",
        "Simplified administration",
        "Flexible eligibility requirements",
        "Optional voluntary benefits"
      ],
      image: "custom-group.png"
    },
    {
      id: "custom-special",
      category: "custom",
      title: "Special Needs Coverage",
      description: "Insurance solutions designed specifically for individuals with unique health considerations or special needs.",
      features: [
        "Specialized coverage for chronic conditions",
        "Access to specialty care providers",
        "Coverage for specialized treatments and therapies",
        "Care coordination services",
        "Support for caregivers"
      ],
      image: "custom-special.png"
    }
  ];

  // Filter services based on active category
  const filteredServices = activeCategory === "all" 
    ? services 
    : services.filter(service => service.category === activeCategory);

  // FAQ data
  const faqs = [
    {
      id: "faq1",
      question: "How do I know which insurance plan is right for me?",
      answer: "Our team of insurance experts can help you evaluate your specific needs, budget, and circumstances to recommend the most suitable insurance plan. We consider factors such as your age, health status, financial situation, and long-term goals to provide personalized recommendations."
    },
    {
      id: "faq2",
      question: "Can I customize my insurance coverage?",
      answer: "Yes, many of our insurance plans offer customization options through riders and additional coverage features. We can work with you to tailor a plan that addresses your specific concerns and priorities while staying within your budget."
    },
    {
      id: "faq3",
      question: "How do I file a claim with my insurance policy?",
      answer: "Filing a claim is simple with Optimal Actuarion. You can initiate a claim by calling our customer service line, through our online portal, or by working directly with your dedicated insurance agent. We strive to make the claims process as smooth and straightforward as possible."
    },
    {
      id: "faq4",
      question: "What makes your senior insurance plans different?",
      answer: "Our senior insurance plans are specifically designed with the unique needs of older adults in mind. We focus on comprehensive coverage for age-related health concerns, offer simplified application processes, provide personalized support, and emphasize affordability to ensure peace of mind during retirement years."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        {/* Announcement bar */}
        <div className="bg-gradient-to-r from-[#002D62] to-[#0077B6] text-white text-center py-2 text-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-semibold mr-2">Limited Time:</span> Get up to 30% off your first year of coverage. 
            <a href="/promotion" className="font-bold underline ml-1 hover:text-blue-100 transition-colors">Learn more</a>
          </div>
        </div>
        
        <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div className="mr-2 text-[#0077B6]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-8 h-8">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-2xl font-extrabold bg-gradient-to-r from-[#002D62] to-[#0077B6] bg-clip-text text-transparent">Optimal Actuarion</span>
              </div>
              <nav className="hidden md:ml-10 md:flex md:space-x-8">
              <a href="/" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Home</a>
                <a href="/#/about" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">About Us</a>
                <a href="/#/services" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2  transition-colors">Services</a>
                <a href="/#/quote" className="text-[#002D62] font-bold border-b-2 border-[#0077B6] px-1 pt-2 pb-1 border-transparent hover:border-[#0077B6] transition-colors">Get a Quote</a>
                <a href="/#/faqs" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">FAQs</a>
                <a href="/#/testimonials" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Testimonials</a>
                <a href="/#/contact" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Contact</a>
               </nav>
            </div>
            <div className="flex items-center">
              <div className="hidden md:flex md:items-center md:space-x-4">
                <a href="/login" className="text-[#002D62] font-medium hover:text-[#0077B6] mr-4 transition-colors">Login</a>
                <a href="/quote" className="bg-gradient-to-r from-[#0077B6] to-[#00A8E8] text-white px-6 py-3 rounded-md font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200">
                  Get a Free Quote
                </a>
              </div>
              <div className="flex md:hidden">
                <button
                  type="button"
                  className="text-gray-700 hover:text-[#002D62] transition-colors"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg animate-fadeIn">
            <div className="pt-2 pb-4 space-y-1 px-4">
            <a href="/" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Home</a>
              <a href="/#/about" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">About Us</a>
              <a href="/#/services" className="block py-2 text-[#002D62] font-bold border-l-4 border-[#0077B6] pl-3">Services</a>
              <a href="/#/quote" className="  block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Get a Quote</a>
              <a href="/#/faqs" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">FAQs</a>
              <a href="/#/testimonials" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Testimonials</a>
              <a href="/#/contact" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Contact</a>
             <div className="pt-4 pb-2 space-y-2">
                <a href="/login" className="block text-center text-[#002D62] font-medium hover:text-[#0077B6] py-2 transition-colors">
                  Login
                </a>
                <a href="/quote" className="w-full flex items-center justify-center bg-gradient-to-r from-[#0077B6] to-[#00A8E8] text-white px-4 py-3 rounded-md font-bold shadow-md transition-all hover:shadow-lg">
                  Get a Free Quote
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* Hero section */}
        <div className="bg-[#E8F1F9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <h1 className="text-4xl font-extrabold text-[#002D62] sm:text-5xl">
                Our Insurance Services
              </h1>
              <p className="mt-4 text-lg text-gray-700 max-w-2xl mx-auto">
                Comprehensive insurance solutions tailored to your unique needs with a special focus on senior citizens and underserved communities.
              </p>
            </div>
          </div>
        </div>

        {/* Service categories + services section */}
        <div className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-12 lg:gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-3">
                <div className="sticky top-24 bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-[#002D62] mb-4">Service Categories</h2>
                  <nav className="space-y-1">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${
                          activeCategory === category.id
                            ? 'bg-[#0077B6] text-white font-medium'
                            : 'text-gray-700 hover:bg-[#E8F1F9] hover:text-[#002D62]'
                        }`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </nav>

                  <div className="mt-8 p-4 bg-[#E8F1F9] rounded-lg">
                    <h3 className="text-lg font-medium text-[#002D62]">Need Help?</h3>
                    <p className="mt-2 text-gray-700 text-sm">
                      Our insurance specialists are ready to assist you in finding the perfect coverage.
                    </p>
                    <a 
                      href="/contact" 
                      className="mt-4 block text-center bg-[#0077B6] text-white px-4 py-2 rounded-md font-medium hover:bg-[#002D62] transition-colors"
                    >
                      Contact Us
                    </a>
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="mt-8 lg:mt-0 lg:col-span-9">
                <div className="grid grid-cols-1 gap-8">
                  {filteredServices.map((service) => (
                    <div key={service.id} id={service.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                      <div className="md:flex">
                        <div className="md:w-1/3">
                          <div className="h-64 bg-gray-300">
                            <img 
                              src={service.image} 
                              alt={service.title} 
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.target.src = "/api/placeholder/400/320";
                                e.target.alt = "Insurance Service";
                              }} 
                            />
                          </div>
                        </div>
                        <div className="p-6 md:w-2/3">
                          <h2 className="text-2xl font-bold text-[#002D62]">{service.title}</h2>
                          <p className="mt-2 text-gray-700">{service.description}</p>
                          
                          <div className="mt-4">
                            <h3 className="text-lg font-medium text-[#0077B6]">Key Features:</h3>
                            <ul className="mt-2 space-y-2">
                              {service.features.map((feature, index) => (
                                <li key={index} className="flex items-start">
                                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="mt-6">
                            <a 
                              href="/quote" 
                              className="inline-flex items-center text-[#0077B6] font-medium hover:text-[#002D62] transition-colors"
                            >
                              Get a quote for this plan
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQs about services */}
        <div className="bg-[#F4F4F4] py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#002D62]">Frequently Asked Questions</h2>
              <p className="mt-4 text-lg text-gray-700">
                Get answers to common questions about our insurance services.
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    className="w-full text-left px-6 py-4 flex justify-between items-center focus:outline-none"
                    onClick={() => toggleFaq(faq.id)}
                  >
                    <span className="text-lg font-medium text-[#002D62]">{faq.question}</span>
                    {expandedFaqs[faq.id] ? (
                      <ChevronUp className="h-5 w-5 text-[#0077B6]" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-[#0077B6]" />
                    )}
                  </button>
                  {expandedFaqs[faq.id] && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-700">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-700 mb-4">Still have questions about our services?</p>
              <a 
                href="/faqs" 
                className="inline-flex items-center px-6 py-3 bg-[#0077B6] text-white font-medium rounded-md hover:bg-[#002D62] transition-colors"
              >
                View All FAQs
                <ChevronRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Call to action */}
        <div className="bg-gradient-to-r from-[#002D62] to-[#0077B6] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 text-center">
            <h2 className="text-3xl font-bold">Ready to get started?</h2>
            <p className="mt-4 text-lg max-w-2xl mx-auto">
              Speak with one of our insurance specialists today to find the perfect coverage solution for your needs.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <a
                href="/quote"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-[#002D62] bg-white hover:bg-gray-100 transition-colors"
              >
                Get a Free Quote
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#002D62] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Optimal Actuarion LLC</h3>
              <p className="mb-4">Insurance solutions for everyone, with a special focus on senior citizens and underserved communities.</p>
              <div className="flex flex-col space-y-2">
                <a href="/about" className="hover:text-gray-300">About Us</a>
                <a href="/services" className="hover:text-gray-300">Services</a>
                <a href="/testimonials" className="hover:text-gray-300">Testimonials</a>
                <a href="/faqs" className="hover:text-gray-300">FAQs</a>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4">Contact Information</h3>
              <div className="flex flex-col space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Phone className="h-5 w-5 text-[#0077B6]" />
                  </div>
                  <div className="ml-3">
                    <p>972-891-8635</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Mail className="h-5 w-5 text-[#0077B6]" />
                  </div>
                  <div className="ml-3">
                    <p>OptimalActuarion@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Clock className="h-5 w-5 text-[#0077B6]" />
                  </div>
                  <div className="ml-3">
                    <p>Monday-Friday: 9:00 AM - 5:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4">Office Locations</h3>
              <div className="mb-4">
                <p className="font-medium">Primary Office:</p>
                <p>3221 Finley Road,</p>
                <p>Irving, TX 75062</p>
              </div>
              <div>
                <p className="font-medium">Business Address:</p>
                <p>4020 N MacArthur Blvd,</p>
                <p>Ste 122 #7156,</p>
                <p>Irving, TX 75038</p>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-700">
            <p className="text-center text-sm">© {new Date().getFullYear()} Optimal Actuarion LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}